import React, { useMemo } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $createParagraphNode, $getRoot } from 'lexical';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { CodeNode } from '@lexical/code';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

// Import these from the memory bank note editor to reuse the theme and plugins
import NoteEditorTheme from '../memory-bank/lexical/NoteEditorTheme';
import { ToolbarPlugin } from '../memory-bank/lexical/plugins/ToolbarPlugin';
import { ListMaxIndentLevelPlugin } from '../memory-bank/lexical/plugins/ListMaxIndentLevelPlugin';
import { AutoLinkPlugin } from '../memory-bank/lexical/plugins/AutoLinkPlugin';
import { OnChangeMarkdown } from '../memory-bank/lexical/plugins/OnChangeMarkdown';
import { KeyboardShortcutsPlugin } from '../memory-bank/lexical/plugins/KeyboardShortcutsPlugin';
import '../memory-bank/lexical/lexical-editor.css';

import { MentionNode } from './lexical-mentions/MentionNode';
import { MentionPlugin } from './lexical-mentions/MentionPlugin';
import { MentionSourceContext } from './lexical-mentions/MentionContext';
import { createMentionTransformer } from './lexical-mentions/MentionTransformer';
import type { MentionSource } from './lexical-mentions/types';

export interface MarkdownWYSIWYGEditorProps {
  initialValue?: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: number;
  showToolbar?: boolean;
  readonly?: boolean;
  /** Optional mention sources. Pass one per entity type (e.g. tools, users). */
  mentionSources?: MentionSource[];
}

function Placeholder({ placeholder }: { placeholder: string }) {
  return (
    <div className="absolute top-3 left-3 text-gray-400 pointer-events-none select-none">
      {placeholder}
    </div>
  );
}

// Always include MentionNode so it can be serialised / deserialised even
// when no sources are provided (keeps the registered node set static).
const BASE_NODES = [
  HeadingNode,
  ListNode,
  ListItemNode,
  QuoteNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  AutoLinkNode,
  LinkNode,
  CodeNode,
  MentionNode,
];

export function MarkdownWYSIWYGEditor({
  initialValue = '',
  readonly = false,
  placeholder = 'Write your note here...',
  height = 200,
  showToolbar = true,
  onChange,
  mentionSources = [],
}: MarkdownWYSIWYGEditorProps) {
  const mentionTransformer = useMemo(
    () => createMentionTransformer(mentionSources),
    [mentionSources],
  );
  const allTransformers = useMemo(
    () => [...TRANSFORMERS, mentionTransformer],
    [mentionTransformer],
  );

  return (
    <MentionSourceContext.Provider value={mentionSources}>
      <LexicalComposer
        initialConfig={{
          namespace: 'markdown-wysiwyg-editor',
          // Lexical controls editability here — NOT via a `readOnly` attribute
          // on ContentEditable (that spreads onto a <div> as a no-op).
          editable: !readonly,
          theme: NoteEditorTheme,
          onError(error: Error) {
            console.error('Lexical editor error:', error);
          },
          nodes: BASE_NODES,
          editorState: () => {
            let str = (initialValue || '').replace(/\n\n<br>\n/g, '\n');

            // If we still have br tags, clean them up
            if (str.match(/<br>/g)) {
              str = str
                .replace(/^(\n)(?=\s*[-+\d.])/gm, '')
                .replace(/<br>/g, '');
            }

            str = str
              // Unescape HTML characters
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, '&')
              .replace(/&#39;/g, "'")
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>');

            if (!str) {
              // Initialize with empty paragraph for proper focus behavior
              const root = $getRoot();
              const paragraph = $createParagraphNode();
              root.append(paragraph);
              return;
            }

            $convertFromMarkdownString(str, allTransformers);
          },
        }}
      >
        <div
          className={`border border-gray-300 rounded-md overflow-hidden bg-white`}
        >
          {showToolbar && <ToolbarPlugin />}
          <div className="relative" style={{ minHeight: height }}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="lexical-editor outline-none resize-none p-3 text-sm leading-relaxed"
                  style={{ minHeight: height }}
                />
              }
              placeholder={<Placeholder placeholder={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
        </div>

        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <OnChangeMarkdown onChange={onChange} transformers={allTransformers} />
        <AutoLinkPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <MarkdownShortcutPlugin transformers={allTransformers} />
        <KeyboardShortcutsPlugin />
        {mentionSources.length > 0 && (
          <MentionPlugin sources={mentionSources} />
        )}
      </LexicalComposer>
    </MentionSourceContext.Provider>
  );
}
